import React, { useState, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

// Hook

// Component
import ModalContainer from "components/ModalContainer";
import GlobalAlert from "components/Notification";
import Loading from "../loading";

// Util
import { defaultMacAddressObj } from "../util/const";
import { toJSON } from "../util/func";
import Api from "../util/api";
import Form from "../util/form";
import MainView from "./MainView";

// Const
const viewStateEnum = {
  MAIN: "main",
  ADD: "add",
  UPLOAD: "upload",
  IMPORT: "import",
  FROM_CLIENT: "from_client",
};

const getViewStateTitle = (view, selectedMacAddress) => {
  const viewStateTitleMap = {
    [viewStateEnum.MAIN]: selectedMacAddress ? "Edit MAC Group" : "Create New MAC Group",
    [viewStateEnum.ADD]: "Add MAC Address",
    [viewStateEnum.UPLOAD]: "Upload MAC Addresses by Upload",
    [viewStateEnum.IMPORT]: "Import MAC Address List",
    [viewStateEnum.FROM_CLIENT]: "Add from Client List",
  }

  return viewStateTitleMap[view];
}

const ProfileMacAddressModal = ({
  auth,
  uuidList,
  openModal,
  selectedMacAddress,
  handleCloseModal,
  onSaveChanges,
  showEditGeneral,
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth);
  const form = Form.init(theme);

  // State
  const [loading, setLoading] = useState(true);
  const [readyToRenderTable, setReadyToRenderTable] = useState(false);
  const [formValue, setFormValue] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const [viewState, setViewState] = useState(viewStateEnum.MAIN);
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  // Method
  const init = () => {
    setLoading(true);

    if (selectedMacAddress) {
      const { id, name, macList } = selectedMacAddress.ref;

      // Convert macList from "CE:AE:F4:88:22:C5" to "CE-AE-F4-88-22-C5"
      const formattedMacList = macList.map(item => ({
        ...item,
        macAddress: item.macAddress.replace(/:/g, "-")
      }));

      setFormValue({
        id,
        name,
        macAddresses: cloneDeep(formattedMacList),
      });
    } else {
      setFormValue(cloneDeep(defaultMacAddressObj));
    }
    setLoading(false);

    // Defer table rendering slightly to let modal render
    setTimeout(() => setReadyToRenderTable(true), 1000);
  }

  const closeModal = () => {
    setFormValue(cloneDeep(defaultMacAddressObj));
    setSelectedIndexes([]);
    setLoading(true);
    setReadyToRenderTable(false);
    setViewState(viewStateEnum.MAIN);
    handleCloseModal();
  }

  const changeViewState = (newState) => {
    setViewState(newState);
  }

  const getViewTitle = (view) => {
    return getViewStateTitle(view, selectedMacAddress);
  }

  function HandleMainView() {
    return {
      changeFormValue: (key, value) => {
        setFormValue((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
      addMacAddresses: (replaceFlag, newMacAddresses) => {
        const firstMac = newMacAddresses[0]?.macAddress || "";

        // Convert all MAC addresses from ":" to use "-" as separator if the first MAC address uses ":" as separator
        if (firstMac.includes(":")) {
          newMacAddresses.forEach((item, index) => {
            let formattedMac = item.macAddress.toUpperCase().replace(/[:]/g, "-");
            newMacAddresses[index].macAddress = formattedMac;
          });
        }

        // Convert all MAC addresses from "" to "-" as separator if there is no separator in the first MAC address
        if (!firstMac.includes(":") && !firstMac.includes("-")) {
          newMacAddresses.forEach((item, index) => {
            let formattedMac = item.macAddress.toUpperCase();
            if (formattedMac.length === 12) {
              formattedMac = formattedMac.match(/.{1,2}/g).join("-");
            }
            newMacAddresses[index].macAddress = formattedMac;
          });
        }

        setFormValue((prev) => {
          const finalMacAddressItems = [...prev.macAddresses];

          if (replaceFlag) {
            // Combine existing and new MAC addresses, if MAC address exists, overwrite description
            newMacAddresses.forEach((newItem) => {
              // Step 1: collect the index list of existing items when new mac address matches
              // [[0, 8], [1, 9]]...etc
              const matchedIndexes = finalMacAddressItems.reduce((acc, existingItem, index) => {
                if (existingItem.macAddress.toUpperCase() === newItem.macAddress.toUpperCase()) {
                  acc.push(index);
                }
                return acc;
              }, []);

              // Remove the all existing indexes
              if (matchedIndexes.length > 0) {
                for (let i = matchedIndexes.length - 1; i >= 0; i--) {
                  finalMacAddressItems.splice(matchedIndexes[i], 1);
                }
              }

              // Add the new item to the end of list
              finalMacAddressItems.push(newItem);
            });
          } else {
            // Merging
            finalMacAddressItems.push(...newMacAddresses);
          }

          return {
            ...prev,
            macAddresses: finalMacAddressItems,
          };
        });
      },
      changeMacAddressItemValue: (index, key, value) => {
        setFormValue((prev) => {
          const newMacAddresses = [...prev.macAddresses];
          newMacAddresses[index] = {
            ...newMacAddresses[index],
            [key]: value,
          };
          return {
            ...prev,
            macAddresses: newMacAddresses,
          };
        });
      },
      changeDescriptionItemValue: (index, key, value) => {
        setFormValue((prev) => {
          const newMacAddresses = [...prev.macAddresses];
          newMacAddresses[index] = {
            ...newMacAddresses[index],
            [key]: value,
          };
          return {
            ...prev,
            macAddresses: newMacAddresses,
          };
        });
      },
      deleteSelectedMacAddresses: () => {
        setFormValue((prev) => {
          const newMacAddresses = prev.macAddresses.filter((_, index) => !selectedIndexes.includes(index));
          return {
            ...prev,
            macAddresses: newMacAddresses,
          };
        });
        setSelectedIndexes([]);
      },
      checkFormValid: () => {
        if (!formValue.name || formValue.name.trim() === "") {
          return false;
        }

        if (formValue.macAddresses.length === 0) {
          return false;
        }

        for (let macItem of formValue.macAddresses) {
          if (!macItem.macAddress || macItem.macAddress.trim() === "" || !form.validateMacAddress(macItem.macAddress)) {
            return false;
          }
        }

        const macSet = new Set();
        for (let macItem of formValue.macAddresses) {
          const upperMac = macItem.macAddress.toUpperCase();
          if (macSet.has(upperMac)) {
            return false;
          }
          macSet.add(upperMac);
        }

        return true;
      },
      handleSave: () => {
        setLoading(true);

        // Convert "CE-AE-F4-88-22-C5" to "CE:AE:F4:88:22:C5" for formValue.macAddresses
        const newMacAddresses = formValue.macAddresses.map((item) => ({
          macAddress: item.macAddress.replace(/-/g, ":"),
          description: item.description,
        }));

        const finalFormValue = {
          name: formValue.name,
          macAddresses: newMacAddresses,
        };

        if (selectedMacAddress) {
          const { id } = selectedMacAddress.ref;
          const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update, id);
          const payload = toJSON(finalFormValue, id);

          sendRequest(
            apiKey,
            uuidList.update,
            params,
            payload,
          );
        } else {
          const payload = toJSON(finalFormValue);
          const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.create);

          sendRequest(
            apiKey,
            uuidList.create,
            params,
            payload,
          );
        }
      }
    }
  }

  // Side effect
  useEffect(() => {
    if (openModal) {
      init();
    } else {
      setFormValue(cloneDeep(defaultMacAddressObj));
      setSelectedIndexes([]);
      setLoading(true);
      setReadyToRenderTable(false);
    }
  }, [openModal]);

  useEffect(() => {
    const createResponse = getData(uuidList.create);

    if (createResponse) {
      if (createResponse.hasOwnProperty("code") && createResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: createResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }

    const updateResponse = getData(uuidList.update);
    if (updateResponse) {
      if (updateResponse.hasOwnProperty("code") && updateResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap]);

  if (!openModal) {
    return null;
  }

  return (
    <>
      <ModalContainer display={openModal} modalWidth="auto">
        {loading && (
          <Loading />
        )}

        {/* Main View */}
        <MainView
          auth={auth}
          viewStateEnum={viewStateEnum}
          getViewTitle={getViewTitle}
          viewState={viewState}
          changeViewState={changeViewState}
          form={form}
          formValue={formValue}
          readyToRenderTable={readyToRenderTable}
          selectedIndexes={selectedIndexes}
          setSelectedIndexes={setSelectedIndexes}
          closeModal={closeModal}
          handleMainView={HandleMainView()}
          showEditGeneral={showEditGeneral}
        />
      </ModalContainer >

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ProfileMacAddressModal;